/// <amd-module name="Build/Queue-Panel/BuildResourcePanel" />
import * as React from "react";

import { BuildResourceData, BuildResourceRunData } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Queue-Panel/Resources";

import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Button } from "VSSUI/Button";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import * as UIResources from "VSSUI/Resources.Input";
import { Spinner } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ColumnSelect, ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import * as TooltipEx from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IBuildResourcePanelProps {
    pageContext: IVssPageContext;
    store: QueueBuildStore;
    resource: BuildResourceData;
    onUpdate(resource: BuildResourceData): void;
    onDismiss(): void;
}

interface IBuildResourcePanelState extends BuildResourceData {
    searchText: string;
    useSelecteRunDisabled: boolean;
}

interface ITableItem extends BuildResourceRunData {
}

export class BuildResourcePanel extends React.Component<IBuildResourcePanelProps, IBuildResourcePanelState> {

    private _isLoading: IObservableValue<boolean>;
    private _selection: ListSelection = new ListSelection({ selectOnFocus: false, multiSelect: false});
    private _selectedRun: string;
    private _selectedRunName: string;
    private _runs: IObservableArray<BuildResourceRunData>;
    private _filteredRuns: IObservableArray<BuildResourceRunData>;

    constructor(props: IBuildResourcePanelProps) {
        super(props);
        this._isLoading = new ObservableValue(false);
        this._runs =  new ObservableArray([]);
        this._filteredRuns =  new ObservableArray([]);
        const state = { ...props.resource, searchText: "" ,useSelecteRunDisabled: false };
        this.state = state;
    }

    public componentDidMount() {
        this._initializeResourceVersions();
    }
    
    public render(): JSX.Element {
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={Resources.BuildResourcePanelDescription}
                escDismiss={true}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: this.props.resource.name }}
                className="pipeline-resource-panel"
                footerButtonProps={[
                    {
                        text: Resources.CancelButtonText,
                        onClick: this.props.onDismiss
                    },
                    {
                        text: Resources.BuildResourceUpdateButtonText,
                        disabled: this.state.useSelecteRunDisabled,
                        primary: true,
                        onClick: this._update
                    }]}>
                <div className="margin-vertical-8 flex-grow">
                    {this._getSearchBar()}
                    {this._buildVersionsTableComponent()}
                </div>
            </Panel>
        );
    }

    public componentWillUnmount() {
        this.props.store.buildResourceRuns && this.props.store.buildResourceRuns.unsubscribe(this._updateResourceRuns);
    }

    private async _initializeResourceVersions() {
        if (!this.props.store.getBuildResourceRuns(this.state.alias)) {
            this._isLoading.value = true;
            await this.props.store.fetchBuildResourceRuns(this.state.alias, this.state.connectionId, this.state.buildType, this.state.name, this.state.branch);
            this._isLoading.value = false;
        }
        this._updateResourceRuns();
        this.props.store.buildResourceRuns && this.props.store.buildResourceRuns.subscribe(this._updateResourceRuns);
    }

    private _buildVersionsTableComponent(): React.ReactNode {
        return (
                <div className={ "flex flex-grow flex-column"}>
                    <Observer loading={this._isLoading}  versions={this._filteredRuns}>
                        {(props: { loading: boolean, versions: BuildResourceRunData[] }) => props.loading
                            ? (<div><Spinner className="queue-build-spinner" /></div>)
                            : (
                                <Observer errorMessage={this.props.store.errorMessage} versions={this._filteredRuns}>
                                    {(props: { errorMessage: string, versions: BuildResourceRunData[] }) =>
                                        {
                                            if (props.errorMessage) {
                                                return (
                                                    <div className="margin-vertical-8 padding-horizontal-20">
                                                        <MessageCard className="multiline-error" severity={MessageCardSeverity.Error}>{props.errorMessage} </MessageCard>
                                                    </div>
                                                );
                                            } else if (props.versions.length > 0) {
                                                return (<Table
                                                            selection={this._selection} 
                                                            onSelect={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<BuildResourceRunData>): void => {
                                                                let disableSelectedButton: boolean = false;
                                                                if (this._selection.selectedCount == 0) {
                                                                    disableSelectedButton = true;
                                                                }
                                                                this.setState({useSelecteRunDisabled: disableSelectedButton});
                                                                this._selectedRun = tableRow.data.runId;
                                                                this._selectedRunName = tableRow.data.buildName;
                                                            }}
                                                            columns={this._tableColumns}
                                                            itemProvider={this._getItemProvider(props.versions)} />);
                                            } else {
                                                const message: string = this.state.searchText ? format(Resources.BuildResourceNoSearchResultsText, this.state.searchText) :Resources.BuildResourceNoRunsText;
                                                return (<div className="margin-vertical-8 padding-horizontal-20">
                                                            <MessageCard severity={MessageCardSeverity.Info}>{message}</MessageCard>
                                                        </div>);
                                            }
                                        }
                                    }
                                </Observer>
                            )}
                    </Observer>
                </div>
        );
    }

    private _renderRunCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        const runLine = (<TooltipEx.Tooltip text={tableItem.runId} overflowOnly={true}>
                            <span className="text-ellipsis primary-text">{tableItem.buildName}</span>
                        </TooltipEx.Tooltip>);

        return ( 
            <TwoLineTableCell 
                columnIndex={columnIndex}  
                tableColumn={tableColumn} 
                key={"col-" + columnIndex}
                line1={runLine} 
                line2={<span></span>}/>);
    }

    private _getSearchBar(): JSX.Element {
        return (
            <TextField
                placeholder={Resources.BuildResourceSearchBarText}
                className="margin-horizontal-20 margin-bottom-8"
                onChange={this._onChangeSearchText}
                prefixIconProps={{ iconName: "Search" }}
                suffixIconProps={
                    this.state.searchText
                    ?   {
                            render: className => {
                                return (
                                    <Button
                                        ariaLabel={UIResources.ClearFilter}
                                        className={css(className, "bolt-text-filterbaritem-clear")}
                                        iconProps={{ iconName: "Cancel"}}
                                        onClick={this._onClickClearButton}
                                    />
                                );
                            }
                        }
                    : undefined
                }
                value={this.state.searchText}
            />
        );
    }

    private _getItemProvider(runs: BuildResourceRunData[]): ArrayItemProvider<ITableItem> {
        const rows: ITableItem[] = runs.map<ITableItem>(run => {
            return run as ITableItem;
        });
        return new ArrayItemProvider<ITableItem>(rows);
    }

    private _update = () => {
        const resource = { ...this.state };
        resource.version = this._selectedRun;
        resource.versionName = this._selectedRunName;
        this.props.onUpdate(resource);
        this.props.onDismiss();
    }

    private _onChangeSearchText = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
        this._searchBuildRuns(newValue);
    }

    private _onClickClearButton = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this._filteredRuns.value = this._runs.value;
        this._updateSelection(this._filteredRuns.value);
        this.setState({
            searchText: ""
        });
    };

    private _searchBuildRuns = (searchValue: string): void => {
        const searchValueLower = searchValue.toLowerCase();
        const filteredRuns = this._runs.value.filter(r => r.runId.toLowerCase().indexOf(searchValueLower) > -1);
        this._filteredRuns.value = filteredRuns;
        this._updateSelection(filteredRuns);
        this.setState({
            searchText: searchValue
        });
    }

    private _updateSelection = (runs: BuildResourceRunData[]): void => {
        let isRunSelected = false;
        if (this.state.version) {
            runs.some((run, index) => {
                if (equals(run.runId, this.state.version)) {
                    this._selection.select(index);
                    this.setState({
                        useSelecteRunDisabled: false
                    });
                    this._selectedRun = run.runId;
                    this._selectedRunName = run.buildName;
                    isRunSelected = true;  
                    return true;
                }
            });
            
            //clear the previous selection as none of the runs match the version
            if(!isRunSelected) {
                this._selection.clear();
            }
        } 
        else if(!this.state.searchText) {
            //selecting the latest run as default in case of no searchText and no version selected
            if (runs.length > 0) {
                this._selection.select(0);
                this.setState({
                    version: runs[0].runId,
                    versionName: runs[0].buildName,
                    useSelecteRunDisabled: false
                });
            }
        }
    }

    private _updateResourceRuns = (): void => {
        const runs: BuildResourceRunData[] = [];
        if (this.props.store.buildResourceRuns) {
            const buildRuns: BuildResourceRunData[] | undefined = this.props.store.buildResourceRuns.get(this.state.alias);
            if (buildRuns && buildRuns.length > 0){
                runs.push(...buildRuns);
            }
        }

        this._runs.value = runs;
        if (this.state.searchText) {
            this._searchBuildRuns(this.state.searchText);
        }
        else {
            this._filteredRuns.value = runs;
            this._updateSelection(runs);
        }
    }

    private _tableColumns: ITableColumn<ITableItem>[] = [
        new ColumnSelect(), {
            id: "pipeline-run",
            width: 404,
            name: Resources.BuildRunColumnHeadingForBuildResourcePanel,
            renderCell: this._renderRunCell
        }
    ];
}
